import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { IRole } from "core/types/base/IRole";
import { IBaseModalProps } from "core/types/elements/IModal";
import { toggleInvalidInput, useAppSelector } from "core/helpers";
import { API } from "constants/RestApi";
import useBaseAction from "hooks/core/useBaseActions";
import { toast } from "react-toastify";
import { IPermissionCategory } from "core/types/base/IPermissions";
import BaseModal from "../BaseModal/BaseModal";
import CustomTooltip from "../CustomTooltip/CustomTooltip";

const CreateEditRoleModal = ({
  showModal,
  onCancel,
  onClose,
  onSave,
  selectedEntity,
}: IBaseModalProps<IRole> & { selectedEntity: IRole }) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
  } = useForm<IRole>();

  const [permissions, setPermissions] = useState<IPermissionCategory[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const { request } = useBaseAction();
  const { currentUser } = useAppSelector(state => state.app);

  const handleCheckboxChange = (permissionKey: string) => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionKey)) {
        newSet.delete(permissionKey);
      } else {
        newSet.add(permissionKey);
      }
      return newSet;
    });
  };

  const getPermissions = async () => {
    try {
      const response = await request({
        method: "GET",
        url: API.ADMIN_ROLES_PERMISSIONS,
        useLoader: true,
      });
      console.log("All permissions response:", response);
      setPermissions(response);
    } catch (error: any) {
      console.error("Error fetching permissions:", error);
      toast.error(error?.message || "Failed to fetch permissions.");
    }
  };

  const getRolePermissions = async () => {
    if (!selectedEntity?.id) return;

    try {
      const response = await request({
        method: "GET",
        url: API.ADMIN_ROLE_PERMISSIONS.replace("{id}", selectedEntity.id),
        useLoader: true,
      });
      console.log("Role permissions response:", response);
      if (Array.isArray(response)) {
        setSelectedPermissions(new Set(response));
        console.log("Selected permissions set:", response);
      }
    } catch (error: any) {
      console.error("Error fetching role permissions:", error);
      toast.error(error?.message || "Failed to fetch role permissions.");
    }
  };

  const modalBody = () => {
    return (
      <form className="notification-modal">
        <div className="mb-3">
          <label htmlFor="name" className="form-label required">
            Name
          </label>
          <input
            type="text"
            className={`form-control ${toggleInvalidInput(!!errors.name)}`}
            id="name"
            placeholder="Enter Name"
            {...register("name", { required: true })}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="checkbox" className="form-label">
            Permissions
          </label>
          <div className="permissions-container" style={{ maxHeight: "400px", overflowY: "auto" }}>
            {[...permissions].reverse().map(category => (
              <fieldset key={category.category} className="mb-4 p-3 border rounded">
                <legend className="h6 mb-2">{category.category}</legend>
                <div className="d-flex flex-wrap gap-3">
                  {[...category.permissions].reverse().map(perm => (
                    <div key={perm.key} className="form-check" style={{ width: "fit-content" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={perm.key}
                        checked={selectedPermissions.has(perm.key)}
                        onChange={() => handleCheckboxChange(perm.key)}
                      />
                      <label
                        className="form-check-label d-flex align-items-center gap-1"
                        htmlFor={perm.key}
                      >
                        {perm.description}
                        {perm.key === "Company.Upsert" && (
                          <CustomTooltip
                            bodyText="?"
                            tooltipText="requires Equipment.View, Estimates.View"
                            keyValue={`${perm.key}-requires`}
                            className="text-muted small"
                            usePillStyle
                          />
                        )}
                        {perm.key === "Item.Upsert" && (
                          <CustomTooltip
                            bodyText="?"
                            tooltipText="requires Vendors.View"
                            keyValue={`${perm.key}-requires`}
                            className="text-muted small"
                            usePillStyle
                          />
                        )}
                        {perm.key === "WorkOrder.Upsert" && (
                          <CustomTooltip
                            bodyText="?"
                            tooltipText="requires Calendar.View, Customer.View, Vehicle.View, Equipment.View, Inventory.View"
                            keyValue={`${perm.key}-requires`}
                            className="text-muted small"
                            usePillStyle
                          />
                        )}
                        {perm.key === "Estimate.Upsert" && (
                          <CustomTooltip
                            bodyText="?"
                            tooltipText="requires Customers.View, Inventory.View"
                            keyValue={`${perm.key}-requires`}
                            className="text-muted small"
                            usePillStyle
                          />
                        )}
                        {perm.key === "WorkOrder.Execute" && (
                          <CustomTooltip
                            bodyText="?"
                            tooltipText="requires Customer.View, Vehicle.View, Equipment.View, Inventory.View"
                            keyValue={`${perm.key}-requires`}
                            className="text-muted small"
                            usePillStyle
                          />
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </div>
      </form>
    );
  };

  const onCancelModal = () => {
    reset(undefined);
    setSelectedPermissions(new Set());
    if (onCancel) onCancel();
  };

  const onSubmit: SubmitHandler<IRole> = async formData => {
    const data = {
      ...formData,
      id: selectedEntity?.id,
      tenantId: currentUser?.tenantId || selectedEntity?.tenantId || "",
      permissions: Array.from(selectedPermissions),
    };
    if (onSave) {
      onSave(data, !(!selectedEntity.id && onSave), () => {
        setValue("name", "");
        setSelectedPermissions(new Set());
      });
    }
  };

  useEffect(() => {
    const initializeModal = async () => {
      if (showModal) {
        await getPermissions();
        if (selectedEntity.id) {
          setValue("name", selectedEntity?.name);
          setValue("tenantId", currentUser?.tenantId || selectedEntity?.tenantId || "");
          // If role already has permissions, use them, otherwise fetch from API
          if (selectedEntity.permissions && selectedEntity.permissions.length > 0) {
            console.log("Using permissions from selectedEntity:", selectedEntity.permissions);
            setSelectedPermissions(new Set(selectedEntity.permissions));
          } else {
            await getRolePermissions();
          }
        } else {
          setValue("tenantId", currentUser?.tenantId || selectedEntity?.tenantId || "");
          setSelectedPermissions(new Set());
        }
      }
    };

    initializeModal();

    return () => {
      setSelectedPermissions(new Set());
    };
  }, [showModal, selectedEntity.id]);

  return (
    <BaseModal
      modalTitle={selectedEntity.id ? "Edit Role" : "Create Role"}
      onClose={onClose}
      onCancel={onCancelModal}
      showModal={showModal}
      modalBody={modalBody()}
      onSave={handleSubmit(onSubmit)}
      cancelActionBtnText="Cancel"
      successBtnText={selectedEntity.id ? "Update" : "Create"}
      disableCloseActions
      modalClass="modal-dialog-scrollable"
    />
  );
};

export default CreateEditRoleModal;
